import { createTabSyncChannel as createDefaultTabSyncChannel } from "./tabSyncChannel";

export const DEFAULT_OWNERSHIP_TTL_MS = 5000;
export const DEFAULT_HEARTBEAT_INTERVAL_MS = 1000;

export const LEASE_STORAGE_KEY_PREFIX = "ouro.tabOwnership.lease.";

function leaseStorageKey(userId) {
  return `${LEASE_STORAGE_KEY_PREFIX}${userId}`;
}

function readLease(storage, userId) {
  if (!storage || typeof storage.getItem !== "function") return null;
  let raw;
  try {
    raw = storage.getItem(leaseStorageKey(userId));
  } catch {
    return null;
  }
  if (raw == null || raw === "") return null;
  let o;
  try {
    o = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!o || typeof o !== "object") return null;
  if (
    typeof o.ownerTabId !== "string" ||
    typeof o.epoch !== "number" ||
    typeof o.expiresAtMs !== "number"
  ) {
    return null;
  }
  return o;
}

function writeLease(storage, userId, lease) {
  if (!storage || typeof storage.setItem !== "function") return;
  try {
    storage.setItem(leaseStorageKey(userId), JSON.stringify(lease));
  } catch {
    /* quota / private mode */
  }
}

function removeLease(storage, userId) {
  if (!storage || typeof storage.removeItem !== "function") return;
  try {
    storage.removeItem(leaseStorageKey(userId));
  } catch {
    /* ignore */
  }
}

export function createTabOwnershipCoordinator({
  userId,
  tabId = `tab-${Math.random().toString(36).slice(2, 11)}`,
  storage = typeof localStorage !== "undefined" ? localStorage : null,
  now = () => Date.now(),
  createTabSyncChannel = (opts) => createDefaultTabSyncChannel(opts),
  setInterval: setIntervalFn = typeof setInterval !== "undefined"
    ? setInterval
    : () => 0,
  clearInterval: clearIntervalFn = typeof clearInterval !== "undefined"
    ? clearInterval
    : () => {},
  ownershipTtlMs = DEFAULT_OWNERSHIP_TTL_MS,
  heartbeatIntervalMs = DEFAULT_HEARTBEAT_INTERVAL_MS,
  channelNameBase = "ouro-tab-ownership",
  windowRef = typeof window !== "undefined" ? window : null,
  broadcastChannelFactory,
} = {}) {
  if (!userId) {
    throw new Error("createTabOwnershipCoordinator: userId is required");
  }

  let channel = null;
  let unsubChannel = null;
  let heartbeatId = null;
  let monitorId = null;
  let started = false;
  let releasedEpoch = -1;
  let sessionEnded = false;

  let state = {
    role: "passive",
    epoch: 0,
    ownerTabId: null,
    userId,
  };

  const listeners = new Set();

  function emit() {
    const snapshot = getState();
    listeners.forEach((fn) => {
      try {
        fn(snapshot);
      } catch {
        /* ignore */
      }
    });
  }

  function stopHeartbeat() {
    if (heartbeatId != null) {
      try {
        clearIntervalFn(heartbeatId);
      } catch {
        /* ignore */
      }
      heartbeatId = null;
    }
  }

  function stopMonitor() {
    if (monitorId != null) {
      try {
        clearIntervalFn(monitorId);
      } catch {
        /* ignore */
      }
      monitorId = null;
    }
  }

  function renewLease() {
    if (state.role !== "owner") return;
    writeLease(storage, userId, {
      ownerTabId: tabId,
      epoch: state.epoch,
      expiresAtMs: now() + ownershipTtlMs,
    });
  }

  function startHeartbeat() {
    stopHeartbeat();
    try {
      heartbeatId = setIntervalFn(() => {
        renewLease();
      }, heartbeatIntervalMs);
    } catch {
      heartbeatId = null;
    }
  }

  function checkForStaleLease() {
    if (sessionEnded || state.role === "owner") {
      return;
    }

    const lease = readLease(storage, userId);
    const t = now();
    const fresh =
      lease &&
      typeof lease.expiresAtMs === "number" &&
      t < lease.expiresAtMs;

    if (!fresh) {
      requestOwnership({ reason: "lease-expired" });
    }
  }

  function startMonitor() {
    stopMonitor();
    try {
      monitorId = setIntervalFn(() => {
        checkForStaleLease();
      }, heartbeatIntervalMs);
    } catch {
      monitorId = null;
    }
  }

  function publishOwnershipChanged(extra = {}) {
    if (!channel) return;
    try {
      channel.post({
        type: "ownership-changed",
        userId,
        epoch: state.epoch,
        ownerTabId: tabId,
        ...extra,
      });
    } catch {
      /* ignore */
    }
  }

  function becomeOwner(epoch, extra = {}) {
    stopHeartbeat();
    state = {
      ...state,
      role: "owner",
      epoch,
      ownerTabId: tabId,
    };
    writeLease(storage, userId, {
      ownerTabId: tabId,
      epoch,
      expiresAtMs: now() + ownershipTtlMs,
    });
    startHeartbeat();
    publishOwnershipChanged(extra);
    emit();
  }

  function handleRemoteOwnershipChanged(msg) {
    const e = msg.epoch;
    const oid = msg.ownerTabId;
    if (typeof e !== "number" || typeof oid !== "string") return;
    const lease = readLease(storage, userId);
    const t = now();
    const hasFreshLease =
      lease &&
      typeof lease.expiresAtMs === "number" &&
      t < lease.expiresAtMs;

    if (!hasFreshLease && e <= releasedEpoch) {
      return;
    }

    if (e > state.epoch) {
      stopHeartbeat();
      state = {
        ...state,
        epoch: e,
        ownerTabId: oid,
        role: oid === tabId ? "owner" : "passive",
      };
      if (state.role === "owner") {
        writeLease(storage, userId, {
          ownerTabId: tabId,
          epoch: e,
          expiresAtMs: now() + ownershipTtlMs,
        });
        startHeartbeat();
      } else {
        /* passive: keep storage as reported owner — they hold the lease */
      }
      emit();
      return;
    }

    /* Same epoch: deterministic tie-break so two concurrent claimants do not both demote. */
    if (e === state.epoch && oid !== tabId) {
      if (state.role === "owner") {
        if (oid > tabId) {
          stopHeartbeat();
          state = {
            ...state,
            role: "passive",
            ownerTabId: oid,
          };
          emit();
        } else if (oid < tabId) {
          /* Win tie: reconcile durable lease immediately so new tabs do not read the loser. */
          writeLease(storage, userId, {
            ownerTabId: tabId,
            epoch: state.epoch,
            expiresAtMs: now() + ownershipTtlMs,
          });
          emit();
        }
      } else {
        const leaseAligned =
          hasFreshLease && lease.epoch === e;
        const candidates = [oid];
        if (state.ownerTabId != null && state.ownerTabId !== "") {
          candidates.push(state.ownerTabId);
        }
        if (leaseAligned && typeof lease.ownerTabId === "string") {
          candidates.push(lease.ownerTabId);
        }
        const winner = candidates.reduce((a, b) => (a > b ? a : b));
        if (winner !== state.ownerTabId) {
          state = {
            ...state,
            role: "passive",
            ownerTabId: winner,
            epoch: e,
          };
          emit();
        }
      }
    }
  }

  function handleOwnershipReleased() {
    const lease = readLease(storage, userId);
    const t = now();
    const fresh =
      lease &&
      typeof lease.expiresAtMs === "number" &&
      t < lease.expiresAtMs;

    if (fresh && lease.ownerTabId === tabId) {
      if (state.role === "owner") {
        return;
      }
      state = {
        ...state,
        role: "owner",
        epoch: lease.epoch,
        ownerTabId: tabId,
      };
      startHeartbeat();
      emit();
      return;
    }

    stopHeartbeat();
    if (!lease || !fresh) {
      requestOwnership({ reason: "owner-released" });
      return;
    } else {
      state = {
        ...state,
        role: "passive",
        ownerTabId: lease.ownerTabId,
        epoch: lease.epoch,
      };
    }
    emit();
  }

  function applySessionEndedLocal() {
    sessionEnded = true;
    stopHeartbeat();
    stopMonitor();
    removeLease(storage, userId);
    state = {
      ...state,
      role: "passive",
      epoch: 0,
      ownerTabId: null,
    };
    emit();
  }

  function handleMessage(msg) {
    if (!msg || typeof msg !== "object") return;
    if (msg.type === "session-ended" && msg.userId === userId) {
      applySessionEndedLocal();
      return;
    }
    if (msg.type === "ownership-released" && msg.userId === userId) {
      handleOwnershipReleased();
      return;
    }
    if (msg.type === "ownership-changed" && msg.userId === userId) {
      handleRemoteOwnershipChanged(msg);
    }
  }

  function bootstrap() {
    const lease = readLease(storage, userId);
    const t = now();
    const fresh =
      lease &&
      typeof lease.expiresAtMs === "number" &&
      t < lease.expiresAtMs;

    if (!lease || !fresh) {
      const nextEpoch = (lease?.epoch ?? 0) + 1;
      becomeOwner(nextEpoch);
      return;
    }

    if (lease.ownerTabId === tabId) {
      state = {
        ...state,
        role: "owner",
        epoch: lease.epoch,
        ownerTabId: tabId,
      };
      renewLease();
      startHeartbeat();
      emit();
      return;
    }

    state = {
      ...state,
      role: "passive",
      epoch: lease.epoch,
      ownerTabId: lease.ownerTabId,
    };
    emit();
  }

  function getState() {
    return {
      role: state.role,
      epoch: state.epoch,
      ownerTabId: state.ownerTabId,
      userId: state.userId,
    };
  }

  function isOwner() {
    return state.role === "owner";
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      return () => {};
    }
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function start() {
    if (started) return;
    started = true;
    sessionEnded = false;
    channel = createTabSyncChannel({
      channelName: `${channelNameBase}:${userId}`,
      storage,
      windowRef,
      broadcastChannelFactory,
    });
    unsubChannel = channel.subscribe(handleMessage);
    startMonitor();
    bootstrap();
  }

  function stop() {
    if (!started) return;
    if (state.role === "owner") {
      const lease = readLease(storage, userId);
      const mayEraseOurLease =
        lease != null &&
        lease.ownerTabId === tabId &&
        lease.epoch <= state.epoch;
      if (mayEraseOurLease) {
        removeLease(storage, userId);
      }
      stopHeartbeat();
      state = {
        ...state,
        role: "passive",
        ownerTabId: null,
      };
      if (unsubChannel) {
        try {
          unsubChannel();
        } catch {
          /* ignore */
        }
        unsubChannel = null;
      }
      if (mayEraseOurLease) {
        try {
          channel?.post({
            type: "ownership-released",
            userId,
            releasedByTabId: tabId,
          });
        } catch {
          /* ignore */
        }
      }
    }
    started = false;
    stopHeartbeat();
    stopMonitor();
    if (unsubChannel) {
      try {
        unsubChannel();
      } catch {
        /* ignore */
      }
      unsubChannel = null;
    }
    if (channel) {
      try {
        channel.close();
      } catch {
        /* ignore */
      }
      channel = null;
    }
  }

  function requestOwnership(opts = {}) {
    if (sessionEnded) {
      return;
    }
    const { reason, ...rest } = opts;
    const lease = readLease(storage, userId);
    const t = now();
    const leaseStale =
      !lease ||
      typeof lease.expiresAtMs !== "number" ||
      t >= lease.expiresAtMs;
    const baseEpoch = Math.max(
      state.epoch,
      typeof lease?.epoch === "number" ? lease.epoch : 0
    );
    const nextEpoch = baseEpoch + 1;
    becomeOwner(nextEpoch, {
      ...rest,
      ...(reason != null ? { reason } : {}),
      ...(leaseStale ? { staleTakeover: true } : {}),
    });
  }

  function broadcastSessionEnded() {
    if (channel) {
      try {
        channel.post({ type: "session-ended", userId });
      } catch {
        /* ignore */
      }
    }
    applySessionEndedLocal();
  }

  return {
    start,
    stop,
    getState,
    isOwner,
    subscribe,
    requestOwnership,
    broadcastSessionEnded,
  };
}
